import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { Box } from "../../components/ui/box";
import { Calendar } from "../../components/ui/calendar";
import { Icon } from "../../components/ui/icon";
import {
  Popover,
  PopoverPopup,
  PopoverTrigger,
} from "../../components/ui/popover";
import { Separator } from "../../components/ui/separator";
import { Spinner } from "../../components/ui/spinner";
import { Text } from "../../components/ui/text";
import { formatDateKey } from "./date-label";
import { omitUndefined, type Omittable } from "../../lib/props";

type DatePillProps = Omittable<{
  label: string;
  /** Async — scrolls to the target day, paginating forward if needed. Resolves
   *  as soon as the scroll starts (which is when the popover should close). */
  scrollToDate?: (dateKey: string) => Promise<void> | void;
  /** All YYYY-MM-DD days the feed has visible content on, sorted descending.
   *  Source-independent of pagination where the feed can supply it (e.g. a
   *  server-reported list); otherwise derived from loaded items. The popover
   *  uses this to pick a real target dateKey for each option (e.g. "Last week"
   *  → the most recent content day on or before 7 days ago). */
  dateKeys?: string[];
}>;

/**
 * The clickable date pill with optional "Jump to…" popover.
 * Used both inline (inside DateSeparator) and as a floating overlay.
 */
export function DatePill({ label, scrollToDate, dateKeys }: DatePillProps) {
  const [open, setOpen] = useState(false);
  // dateKey of the option currently being loaded — null when idle.
  // The popover stays open with a spinner next to that option until the
  // target day's group is reached and scrolling begins.
  const [loadingDateKey, setLoadingDateKey] = useState<string | null>(null);
  // Synchronous mirror of loadingDateKey — React state isn't updated
  // synchronously, so this ref is what actually gates a second handleJump
  // call from a doubled event.
  const loadingDateKeyRef = useRef<string | null>(null);
  // When true, the popover swaps the targets list for a calendar.
  const [pickingDate, setPickingDate] = useState(false);
  // True while a date picked from the calendar is paginating + scrolling.
  // Drives the spinner in the "Jump to a specific date..." row of the
  // targets list (the popover swaps back to the list after the pick).
  const [loadingPickedDate, setLoadingPickedDate] = useState(false);

  // Reset to the targets view whenever the popover closes.
  useEffect(() => {
    if (!open) setPickingDate(false);
  }, [open]);

  // Resolves each option to a concrete YYYY-MM-DD content day or null. Targets
  // with a null dateKey are hidden by the renderer.
  //   Today        → exact match for today's local day, else null
  //   Yesterday    → exact match for yesterday's local day, else null
  //   Last week    → earliest content day in the previous Mon–Sun, else null
  //   Last month   → earliest content day in the previous calendar month, else null
  //   The very beginning → oldest content day overall, else null
  //
  // All math is in the viewer's local zone — feeds key dateKeys by that same
  // zone and group DOM ids by local-day, so the sources of dateKeys agree.
  const jumpTargets = useMemo((): {
    label: string;
    dateKey: string | null;
  }[] => {
    const all = dateKeys ?? [];
    if (!all.length) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayKey = formatDateKey(today);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayKey = formatDateKey(yesterday);

    // Last week = previous Mon–Sun (ISO).
    const dayOfWeek = today.getDay(); // Sun=0..Sat=6
    const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const thisMonday = new Date(today);
    thisMonday.setDate(today.getDate() - daysSinceMonday);
    const lastWeekStart = new Date(thisMonday);
    lastWeekStart.setDate(thisMonday.getDate() - 7);
    const lastWeekEnd = new Date(thisMonday);
    lastWeekEnd.setDate(thisMonday.getDate() - 1);
    const lastWeekStartKey = formatDateKey(lastWeekStart);
    const lastWeekEndKey = formatDateKey(lastWeekEnd);

    // Last month = previous calendar month.
    const lastMonthStart = new Date(
      today.getFullYear(),
      today.getMonth() - 1,
      1,
    );
    const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
    const lastMonthStartKey = formatDateKey(lastMonthStart);
    const lastMonthEndKey = formatDateKey(lastMonthEnd);

    // dateKeys is sorted desc → the LAST in-range entry is the earliest.
    const earliestInRange = (
      startKey: string,
      endKey: string,
    ): string | null => {
      const inRange = all.filter((d) => d >= startKey && d <= endKey);
      return inRange[inRange.length - 1] ?? null;
    };

    return [
      { label: "Today", dateKey: all.includes(todayKey) ? todayKey : null },
      {
        label: "Yesterday",
        dateKey: all.includes(yesterdayKey) ? yesterdayKey : null,
      },
      {
        label: "Last week",
        dateKey: earliestInRange(lastWeekStartKey, lastWeekEndKey),
      },
      {
        label: "Last month",
        dateKey: earliestInRange(lastMonthStartKey, lastMonthEndKey),
      },
      { label: "The very beginning", dateKey: all[all.length - 1] ?? null },
    ];
  }, [dateKeys]);

  const handleJump = useCallback(
    async (dateKey: string) => {
      if (!scrollToDate) return;
      // Synchronous bail — second call from a doubled tap event won't even
      // start a parallel scrollToDate.
      if (loadingDateKeyRef.current != null) return;
      loadingDateKeyRef.current = dateKey;
      setLoadingDateKey(dateKey);
      try {
        await scrollToDate(dateKey);
      } finally {
        // scrollToDate resolves the moment the smooth scroll begins, so
        // closing here means the popover stays visible (with its spinner)
        // through any pagination, then dismisses just as the page starts
        // moving toward the target.
        loadingDateKeyRef.current = null;
        setLoadingDateKey(null);
        setOpen(false);
      }
    },
    [scrollToDate],
  );

  const hasPopover = !!dateKeys?.length && !!scrollToDate;

  const pillProps = {
    background: "background" as const,
    rounded: "full" as const,
    border: true,
    direction: "row" as const,
    align: "center" as const,
    gap: "xs" as const,
    paddingLeft: "sm" as const,
    paddingRight: (hasPopover ? "xs" : "sm") as "xs" | "sm",
    paddingY: "xs" as const,
  };

  const pillBody = (
    <>
      <Text size="xs" variant="muted" weight="semibold">
        {label}
      </Text>
      {hasPopover && <Icon name="chevron-down" size="xs" color="muted" />}
    </>
  );

  if (!hasPopover) return <Box {...pillProps}>{pillBody}</Box>;

  // Single popover — content swaps between targets list and calendar based
  // on `pickingDate`. The popover stays open throughout the swap; closing
  // happens via setOpen(false) once the scroll has started.
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger render={<Box as="button" {...pillProps} />}>
        {pillBody}
      </PopoverTrigger>
      <PopoverPopup
        side="bottom"
        align="center"
        // The calendar swaps in after the positioner measured the list, so
        // an auto width would stay pinned at the list's 240px and clip the
        // last column; max-content lets the popup grow to the calendar.
        className={pickingDate ? "w-max" : "w-60"}
        {...(pickingDate ? {} : { padding: "none" as const })}
        // Block "return focus to the trigger" on close — the browser then
        // auto-scrolls to make the trigger visible, which fights our smooth
        // scrollToDate.
        finalFocus={false}
      >
        {pickingDate ? (
          <Calendar
            mode="single"
            // react-day-picker hands back local-midnight dates, and dateKeys
            // are local-day keys — read them in the local zone (formatDateKey)
            // so non-UTC viewers don't disable/mis-target the real content day.
            disabled={(date: Date) =>
              !(dateKeys ?? []).includes(formatDateKey(date))
            }
            {...omitUndefined({
              defaultMonth: dateKeys?.[0]
                ? new Date(`${dateKeys[0]}T00:00:00`)
                : undefined,
            })}
            onSelect={async (date: Date | undefined) => {
              if (!date) return;
              const key = formatDateKey(date);
              if (!(dateKeys ?? []).includes(key)) return;
              // Swap the popover content back to the targets list and put
              // the spinner on the "Jump to a specific date..." row while
              // handleJump paginates + scrolls. handleJump's finally fires
              // setOpen(false) once the scroll starts, closing the popover.
              setPickingDate(false);
              setLoadingPickedDate(true);
              try {
                await handleJump(key);
              } finally {
                setLoadingPickedDate(false);
              }
            }}
          />
        ) : (
          // Edge-to-edge sections: the popup carries no padding so the
          // hairline spans its full width; each section pads its own rows.
          <Box direction="column">
            {/* 12px inset = the rows' 4px section pad + 8px row pad. */}
            <Box paddingX="base" paddingTop="sm" paddingBottom="2xs">
              <Text size="xs" variant="muted" weight="medium">
                Jump to...
              </Text>
            </Box>

            <Box direction="column" paddingX="xs" paddingBottom="xs">
              {jumpTargets
                .filter(({ dateKey }) => dateKey != null)
                .map(({ label: optLabel, dateKey }) => (
                  <JumpRow
                    key={optLabel}
                    label={optLabel}
                    loading={loadingDateKey === dateKey}
                    onClick={() => handleJump(dateKey!)}
                  />
                ))}
            </Box>

            <Separator />

            <Box direction="column" padding="xs">
              <JumpRow
                label="Jump to a specific date..."
                loading={loadingPickedDate}
                muted
                onClick={() => setPickingDate(true)}
              />
            </Box>
          </Box>
        )}
      </PopoverPopup>
    </Popover>
  );
}

type JumpRowProps = {
  label: string;
  loading: boolean;
  muted?: boolean;
  onClick: () => void;
};

/** One 32px target row in the "Jump to…" list. */
function JumpRow({ label, loading, muted, onClick }: JumpRowProps) {
  return (
    <Box
      as="button"
      direction="row"
      align="center"
      justify="between"
      gap="sm"
      height={32}
      paddingX="sm"
      rounded="sm"
      hoverBackground
      onClick={onClick}
    >
      <Text size="sm" {...(muted ? { variant: "muted" as const } : {})}>
        {label}
      </Text>
      {loading && <Spinner color="muted" size="sm" />}
    </Box>
  );
}

type DateSeparatorProps = Omittable<{
  label: string;
  scrollToDate?: (dateKey: string) => Promise<void> | void;
  dateKeys?: string[];
}>;

/**
 * A pill-shaped date label centered between two horizontal lines.
 * The pill and lines scroll together — pair with a feed's floating overlay
 * (a fixed DatePill driven by useDateJump's stickyDateLabel) for the
 * sticky-header effect.
 */
export function DateSeparator({
  label,
  scrollToDate,
  dateKeys,
}: DateSeparatorProps) {
  return (
    <Box
      direction="row"
      align="center"
      paddingTop="sm"
      paddingBottom="lg"
      gap="sm"
    >
      <Box background="border" fill="flex" height={1} />
      <DatePill label={label} scrollToDate={scrollToDate} dateKeys={dateKeys} />
      <Box background="border" fill="flex" height={1} />
    </Box>
  );
}
